import React, { useState } from 'react'
import { assetPrefix } from 'config/env_config'
import InputMask from 'react-input-mask'
import EyeHide from 'static/icons/eye-hide.svg'
import EyeShow from 'static/icons/eye-show.svg'
import SearchIcon from 'static/icons/search-icon.svg'
import styled, { css } from 'styled-components'
import uniqid from 'uniqid'

export const Container = styled.div`
  position: relative;
  width: ${({ customWidth }) => customWidth || '100%'};
  display: grid;
  scroll-margin-top: 134px;
  margin: ${({ margin }) => margin || '16px 0 0 0'};
`

export const Label = styled.label`
  position: absolute;
  top: -13px;
  left: 14px;
  background-color: var(--the-white);
  color: var(--gray-5);
  font-size: 12px;
  font-family: var(--primary-font);
  font-weight: 400;
  padding: 2px;
  z-index: 1;
  ${({ hasError }) => hasError && 'color: var(--bronze-gold);'}
`

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`

export const InputStyled = styled.input`
  background-color: var(--the-white);
  border: 1px solid var(--gray-7);
  border-radius: ${({ borderRadius }) => borderRadius || '1.6rem'};
  width: inherit;
  height: ${({ height }) => height || '48px'};
  padding: ${({ padding }) => padding || '1.6rem'};
  outline: none;
  appearance: textfield;
  -moz-appearance: textfield;
  color: ${({ readOnly }) => (readOnly ? 'var(--rain-cloud)' : 'var(--gray-7)')};
  ${({ hasError }) =>
    hasError &&
    `
    color: var(--bronze-gold);
    border: 2px solid var(--bronze-gold);
  `}
  ${({ hasIcon, rightIcon }) => hasIcon && `${rightIcon ? 'padding-right: 38px;' : 'padding-left: 38px;'}`}
  ${({ eye }) => eye && 'padding-right: 38px;'}
  font-size: 16px;
  font-family: var(--primary-font);
  font-weight: 400;

  &::placeholder {
    font-family: var(--primary-font) !important;
    color: var(--gray-7);
    font-size: 14px;
    font-weight: 400;
    line-height: 19.07px;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--the-white) inset !important;
    -webkit-background-clip: text !important;
    background-clip: text !important;
  }

  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:disabled {
    background-color: var(--bleached-silk) !important;
  }

  & + label {
    ${({ labelOnFocus }) => (labelOnFocus) && `
      display: none;
     `
    }
  }

  &:not(:placeholder-shown) + label {
    ${({ labelOnFocus }) => (labelOnFocus) && `
      display: block;
     `
    }
  }

  &:focus + label {
    ${({ labelOnFocus }) => labelOnFocus && `
      display: block;
     `
    }
  }

  &:focus::placeholder {
    color: transparent;
  }
`

export const Eye = styled.div`
  position: absolute;
  right: 16px;
  bottom: 7px;
  ${({ hasError }) =>
    hasError &&
    `
    svg {
      path {
        fill: var(--bronze-gold);
      }
    }
  `}
`

const IconWrapper = styled.div`
  position: absolute;
  height: 40px;
  ${({ right }) => !right ? 'left: 12px;' : 'right: 12px;'}
  bottom: 0;
  display: flex;
  align-items: center;

  img {
    max-width: 2rem;
    max-height: 2rem;

    ${({ hasError }) =>
      !hasError &&
      `
      filter: grayscale(1) opacity(0.4);
    `}
  }
`

const Icon = ({ icon, hasError, right }) => (
  <IconWrapper right={right} hasError={hasError}>
    <img src={`${assetPrefix}/static/icons/${icon}.svg`} alt='icon' />
  </IconWrapper>
)

const Message = styled.div`
  color: var(--gray-7);
  font-size: 10px;
  font-family: var(--primary-font);
  padding: 0 16px;
`

const Error = styled(Message)`
  color: var(--bronze-gold);
  text-align: left;
`

export const Button = styled.button`
  position: absolute;
  top: 0px;
  right: 0;
  width: 48px;
  height: 48px;
  background: transparent;
  border: none;
  &:focus {
    outline: none;
  }
  svg {
    max-width: 20px;
    max-height: 20px;
    width: 20px;
    height: 20px;
    margin: 0px;
    overflow: visible;
    z-index: 1;
    path {
      stroke-width: 1px;
    }
  }
  ${({ disabledButton }) =>
    disabledButton &&
    css`
      pointer-events: none;
      svg {
        path {
          fill: var(--gray-7);
          stroke: transparent;
        }
      }
    `}
`

const Input = React.forwardRef(
  (
    {
      id,
      label,
      name,
      onChange,
      value,
      eye,
      dataTest,
      margin,
      isReadOnly,
      placeholder,
      mask,
      maskChar,
      pattern,
      errorMessage,
      icon,
      rightIcon,
      onBlur,
      onFocus,
      helpText,
      autoComplete,
      hookForm,
      onSearchButtonClick,
      disabledButton = false,
      labelOnFocus,
      height,
      borderRadius,
      padding,
      ...props
    },
    ref
  ) => {
    const [type, setType] = useState(props.type || 'text')

    const handleChange = (e) => {
      if (onChange) onChange(e)
    }

    const handleVisibilityChange = () => {
      setType(type === 'password' ? 'text' : 'password')
    }

    const componentId = uniqid()
    const hasError = !!errorMessage
    const hasIcon = !!icon || !!rightIcon
    const inputProps = {
      ...props,
      id: id || componentId,
      name,
      onChange: handleChange,
      type,
      value,
      'data-test': dataTest,
      readOnly: isReadOnly,
      placeholder,
      hasError,
      hasIcon,
      rightIcon: !!rightIcon,
      eye: eye,
      onBlur,
      autoComplete,
      ref,
      labelOnFocus,
      height,
      borderRadius,
      padding,
      onFocus,
      ...hookForm
    }

    const EYE_ICON = {
      password: <EyeHide />,
      text: <EyeShow />
    }

    const input = mask ? (
      <InputMask
        {...inputProps}
        mask={mask}
        maskChar={maskChar}
        pattern={pattern}
        alwaysShowMask
      >
        {(inputProps) => <InputStyled {...inputProps} placeholder={placeholder} />}
      </InputMask>
    ) : (
      <InputStyled {...inputProps} />
    )

    const renderIcon = () => {
      if (hasIcon && icon !== 'search') {
        return <Icon right={!!rightIcon} icon={icon ?? rightIcon} hasError={hasError} />
      } else if (icon === 'search') {
        return (
          <Button onClick={onSearchButtonClick} disabledButton={disabledButton}>
            <SearchIcon />
          </Button>
        )
      }
      return null
    }

    return (
      <Container margin={margin} customWidth={props.customWidth}>
        <InputContainer>
          {renderIcon()}
          {input}
          {label && <Label labelOnFocus={labelOnFocus} hasError={hasError}>{label}</Label>}
          {eye && (
            <Eye hasError={hasError} onClick={handleVisibilityChange}>
              {EYE_ICON[type]}
            </Eye>
          )}
        </InputContainer>
        {helpText && <Message>{helpText}</Message>}
        {hasError && <Error>{errorMessage}</Error>}
      </Container>
    )
  }
)

export default Input
